import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { membershipApi, type Membership } from '../../api/memberships';
import { userApi, type ActivityEntry, type AuditEntry, type User, type UserPreferences } from '../../api/users';

type Tab = 'overview' | 'memberships' | 'preferences' | 'activity' | 'audit';

export function UserDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState('');

  useEffect(() => { void userApi.get(id).then(setUser).catch((e) => setError(String(e))); }, [id]);
  useEffect(() => {
    if (!id) return;
    if (tab === 'memberships') void membershipApi.list({ identity_id: id }).then(setMemberships);
    if (tab === 'preferences') void userApi.getPreferences(id).then(setPrefs);
    if (tab === 'activity') void userApi.getActivity(id).then(setActivity);
    if (tab === 'audit') void userApi.getAudit(id).then(setAudit);
  }, [id, tab]);

  if (!user && !error) return <p className="p-6">Loading…</p>;
  if (!user) return <p className="p-6 text-red-600">{error}</p>;

  const tabs: Tab[] = ['overview', 'memberships', 'preferences', 'activity', 'audit'];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Link to="/users" className="text-sm text-slate-600 hover:underline">← User directory</Link>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-200 text-lg font-medium">
          {user.display_name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{user.display_name}</h1>
          <p className="text-sm text-slate-600">{user.email} · {user.status}</p>
        </div>
      </div>
      <nav className="mt-6 flex gap-1 border-b">
        {tabs.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`px-4 py-2 text-sm capitalize ${tab === t ? 'border-b-2 border-slate-900 font-medium' : 'text-slate-600'}`}>{t}</button>
        ))}
      </nav>
      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid gap-4 rounded-xl border bg-white p-4 text-sm md:grid-cols-2">
            <p>ID: {user.id}</p>
            <p>Username: {user.username || '—'}</p>
            <p>Department: {user.professional?.department || '—'}</p>
            <p>Job title: {user.professional?.job_title || '—'}</p>
          </div>
        )}
        {tab === 'memberships' && (
          <ul className="space-y-2">
            {memberships.map((m) => (
              <li key={m.id} className="rounded-lg border p-3 text-sm">
                <p className="font-medium">{m.scope.type} · {m.status}</p>
                <p className="text-xs text-slate-500">Tenant {m.scope.tenant_id}</p>
              </li>
            ))}
            {memberships.length === 0 && <p className="text-sm text-slate-500">No memberships.</p>}
          </ul>
        )}
        {tab === 'preferences' && prefs && (
          <div className="rounded-xl border bg-white p-4 text-sm">
            <p>Language: {prefs.language}</p>
            <p className="mt-2">Timezone: {prefs.timezone}</p>
            <p className="mt-2">Theme: {prefs.theme}</p>
          </div>
        )}
        {tab === 'activity' && (
          <ul className="space-y-2">{activity.map((a) => (
            <li key={a.id} className="rounded-lg border p-3 text-sm">
              <p className="font-medium">{a.action}</p>
              <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</p>
            </li>
          ))}</ul>
        )}
        {tab === 'audit' && (
          <ul className="space-y-2">{audit.map((a) => (
            <li key={a.id} className="rounded-lg border p-3 text-sm"><p>{a.action} — {a.outcome}</p></li>
          ))}</ul>
        )}
      </div>
    </div>
  );
}
